import { ZfFilesUpload, ZfFormField, ZfIcon, ZfInput, ZfPopover, ZfSelectOption, ZfStack, ZfTypography } from '@zafire/react';
import type { FunctionComponent } from 'react';
import { useRef } from 'react';
import type { Control } from 'react-hook-form';
import { Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import type { DocumentDto, DocumentFileDto, SupportedLanguage } from '../../../api/generated/client';
import { useShadowRootCustomStyles } from '../../../hooks/useShadowRootCustomStyles';
import { AVAILABLE_LANGUAGES } from '../../../i18n/i18nextInit';
import { requiredField } from '../../../utils/form-utils';

interface IProps {
    documentFile: DocumentFileDto;
    name?: string;
    onChange: (manualDocItem: DocumentFileDto, title: string) => void;
    updateFile?: (docFile: DocumentFileDto) => void;
    control: Control<DocumentDto>;
}

export const AdminDocumentFileItem: FunctionComponent<IProps> = ({ documentFile, name, onChange, updateFile, control }) => {
    const { t } = useTranslation();
    const fileInputRef = useRef<HTMLZfFilesUploadElement>(null);

    useShadowRootCustomStyles(fileInputRef, ':host { --zf-icon-size-lg: 20px; --zf-buttons-padding-horizontal-large: 7px; }');

    return (
        <ZfStack direction="row" spacing="medium" alignment="center">
            <ZfStack direction="row" spacing="extra-small" style={{ flex: 1 }}>
                {updateFile ? (
                    <ZfFilesUpload
                        id={`input-file-upload-${documentFile.language}`}
                        ref={fileInputRef}
                        style={{ marginTop: 'var(--padding-top-lg)' }}
                        text=""
                        accept="application/pdf"
                        size="large"
                        hierarchy="tertiary"
                        multiple={false}
                        maxFileSize={5000000}
                        onFileInputChange={(event) => updateFile?.({ ...documentFile, file: event.detail[0], fileName: event.detail[0].name })}
                    />
                ) : null}
                <ZfFormField label={t('contentAdmin.documentFilename')} mandatory={!updateFile} style={{ flex: 1 }}>
                    <ZfInput value={documentFile.fileName} readonly={true} />
                </ZfFormField>
            </ZfStack>
            {documentFile.language ? (
                <Controller
                    name={`name.${documentFile.language}`}
                    control={control}
                    rules={{ required: requiredField(t('textFieldRequired')) }}
                    render={({ field, fieldState }) => (
                        <ZfFormField label={t('contentAdmin.documentTitle')} mandatory={true} style={{ flex: 2 }}>
                            <ZfInput
                                value={field.value ?? ''}
                                error={fieldState.invalid}
                                onInputChange={(e) => {
                                    field.onChange(e.detail);
                                    onChange({ ...documentFile }, e.detail);
                                }}
                                onBlur={field.onBlur}
                            />
                        </ZfFormField>
                    )}
                />
            ) : (
                <ZfFormField label={t('contentAdmin.documentTitle')} mandatory={true} style={{ flex: 2 }}>
                    <ZfPopover alignment="start" slot="label-trailing">
                        <ZfIcon slot="anchor" size="medium" name="info" style={{ cursor: 'pointer' }}></ZfIcon>
                        <ZfTypography variant="label-extra-small" color="normal">
                            {t('dndDisabledTitleFieldTooltip')}
                        </ZfTypography>
                    </ZfPopover>
                    <ZfInput value={name ?? ''} disabled={true} />
                </ZfFormField>
            )}
            <ZfFormField label={t('contentAdmin.languageLabel')} mandatory={!updateFile} style={{ maxWidth: '150px' }}>
                <ZfInput
                    type="select"
                    value={documentFile.language}
                    readonly={!!updateFile}
                    onSelectChange={(e) => onChange({ ...documentFile, language: e.detail as SupportedLanguage }, name ?? '')}
                >
                    {AVAILABLE_LANGUAGES.map((language) => (
                        <ZfSelectOption key={language} value={language} description={language} />
                    ))}
                </ZfInput>
            </ZfFormField>
        </ZfStack>
    );
};
